import type { ConversionMode, KeyAssistStrength } from "./key-assist.ts";
import type { GridValue } from "./music.ts";

export type GuidedIssue = "missing-detail" | "too-many" | "semitone" | "split-notes" | "merged-notes" | "timing";

export type AnalysisSettings = {
  bpm: number;
  grid: GridValue;
  noteDetail: number;
  minimumNoteBeats: number;
  lengthAssist: boolean;
  conversionMode: ConversionMode;
  keyAssistStrength: KeyAssistStrength;
  keyAssistWeight: number;
};

export type ConciergeProposal = {
  issue: GuidedIssue;
  title: string;
  explanation: string;
  settings: AnalysisSettings;
  needsTempoReview: boolean;
  direction: "finer" | "smoother" | "key-assist" | "timing";
};

const GRID_ORDER: GridValue[] = ["1/4", "1/8", "1/16", "1/32"];
const clamp = (value: number) => Math.max(0, Math.min(100, value));
const finerGrid = (grid: GridValue) => GRID_ORDER[Math.min(GRID_ORDER.length - 1, GRID_ORDER.indexOf(grid) + 1)];
const coarserGrid = (grid: GridValue) => GRID_ORDER[Math.max(0, GRID_ORDER.indexOf(grid) - 1)];

export function buildConciergeProposal(issue: GuidedIssue, current: AnalysisSettings): ConciergeProposal {
  if (issue === "missing-detail" || issue === "merged-notes") {
    return {
      issue,
      title: issue === "missing-detail" ? "細かい音を拾いやすくする" : "音の切り替わりを細かく見る",
      explanation: "声の測定はそのまま使い、MIDIへ区切る細かさと音の切り替わり判定を一段だけ上げます。",
      settings: { ...current, grid: finerGrid(current.grid), noteDetail: clamp(current.noteDetail + 15), minimumNoteBeats: Math.min(current.minimumNoteBeats, 0.25) },
      needsTempoReview: false,
      direction: "finer",
    };
  }
  if (issue === "too-many" || issue === "split-notes") {
    return {
      issue,
      title: issue === "too-many" ? "余計な細かい音を減らす" : "1音を途中で分けにくくする",
      explanation: "声の自然な揺れを別の音にしにくい方向へ、MIDIの区切りを一段だけ滑らかにします。",
      settings: { ...current, grid: coarserGrid(current.grid), noteDetail: clamp(current.noteDetail - 18) },
      needsTempoReview: false,
      direction: "smoother",
    };
  }
  if (issue === "semitone") {
    return {
      issue,
      title: "迷った半音だけキーを参考にする",
      explanation: "安定して歌われた半音外の音は残し、境界で迷った音だけ推定キーをヒントに再確認します。",
      settings: { ...current, conversionMode: "key-assist", keyAssistStrength: "normal", keyAssistWeight: 34 },
      needsTempoReview: false,
      direction: "key-assist",
    };
  }
  return {
    issue,
    title: "曲の速さとリズムの細かさを確認する",
    explanation: "マイクはGRIDより細かく音程を測っています。BPMとGRIDは、その結果をMIDIの時間軸へ並べる位置と長さに影響します。",
    settings: { ...current },
    needsTempoReview: true,
    direction: "timing",
  };
}
